import { Arrow, ArrowRight, Check, Euro } from "@/components/landing/icons";
import { d } from "@/components/landing/Sections";
import { COUP_DE_POUCE_DATE_FIN, MENTION_CONDITIONS, STAT_PRISE_EN_CHARGE } from "@/config/site";
import { REALISATIONS } from "@/data/realisations";

export const HeroHighlight = () => (
  <div className="lp-hl lp-rv" style={d(90)} data-testid="hero-courtage-highlight">
    <p><strong>0 € de frais de courtage.</strong> Travaux et accompagnement financés à 100 % par les CEE dans {STAT_PRISE_EN_CHARGE}.<sup>*</sup></p>
    <small>* {MENTION_CONDITIONS}</small>
  </div>
);

const COST = [
  ["01", "Les fournisseurs d'énergie financent la prime CEE", "Ils sont obligés par l'État de financer des économies d'énergie."],
  ["02", "La prime couvre vos travaux ET notre accompagnement", "Airthermo est rémunéré sur la prime du dossier, jamais par vous."],
  ["03", `Vous : 0 € dans ${STAT_PRISE_EN_CHARGE}`, "Aucun honoraire, aucune facture Airthermo, aucune avance."],
];

export const CostSection = () => (
  <section className="lp-section lp-cost" id="cout" data-testid="cost-section">
    <div className="lp-wrap">
      <div className="lp-head"><div><span className="lp-eyebrow lp-rv">Le courtage ne vous coûte rien</span><h2 className="lp-rv" style={d(60)}>Combien vous coûte Airthermo ? <em>0 €.</em></h2></div></div>
      <ol className="lp-cost-flow">
        {COST.map(([n, t, x], i) => (
          <li key={n} className="lp-rv" style={d(i * 90)} data-testid={`cost-step-${i + 1}`}>
            <div className={`lp-bezel${i === 2 ? " lp-cost-last" : ""}`}><div className="lp-core"><span className="lp-cost-n">{n}</span><h3>{t}</h3><p>{x}</p></div></div>
            {i < 2 && <span className="lp-cost-arrow" aria-hidden="true"><ArrowRight /></span>}
          </li>
        ))}
      </ol>
      <div className="lp-cost-foot lp-rv">
        <p className="lp-mention" data-testid="cost-mention">{MENTION_CONDITIONS}</p>
        <a href="#etude" className="lp-btn" data-testid="cost-cta">Vérifier mon éligibilité gratuitement<span className="lp-ico"><Arrow /></span></a>
      </div>
    </div>
  </section>
);

export const CourtageBox = () => (
  <div className="lp-wrap"><div className="lp-courtage lp-rv" data-testid="courtage-box">
    <span className="lp-k lp-c0"><Euro /></span>
    <div>
      <strong>0 € de frais de courtage</strong>
      <p>Vous ne payez jamais Airthermo : le cabinet est rémunéré sur la prime CEE du dossier, via l'installateur et le financeur des CEE. Dans {STAT_PRISE_EN_CHARGE}, les travaux et notre accompagnement sont financés à 100 % par les CEE.</p>
      <small>{MENTION_CONDITIONS}</small>
    </div>
  </div></div>
);

const CDP = {
  tertiaire: "Coup de pouce Chauffage : en tertiaire, la prime CEE est multipliée par 3 quand une chaudière gaz ou fioul est remplacée par une pompe à chaleur.",
  residentiel: "Bonification Coup de pouce Chauffage possible en remplacement d'une chaudière gaz ou fioul (hors chaudière à condensation).",
};

export const CoupDePouce = ({ kinds = ["tertiaire"] }) => (
  <div className="lp-wrap"><div className="lp-cdp lp-rv" data-testid="coup-de-pouce-banner">
    <span className="lp-cdp-tag">×3</span>
    <div>
      {kinds.map((k) => <p key={k} data-testid={`coup-de-pouce-${k}`}>{CDP[k]}</p>)}
      {COUP_DE_POUCE_DATE_FIN && <small data-testid="coup-de-pouce-date">Offre valable pour les projets engagés avant le {COUP_DE_POUCE_DATE_FIN}.</small>}
    </div>
  </div></div>
);

const PRIMES = [
  ["EHPAD ou clinique", "3 000 m²", "70 000 €"],
  ["Bureaux", "2 000 m²", "50 000 €"],
  ["Commerce", "2 000 m²", "38 000 €"],
];

export const PrimeExamples = () => (
  <section className="lp-section" id="exemples-primes" data-testid="prime-examples">
    <div className="lp-wrap">
      <div className="lp-head"><div><span className="lp-eyebrow lp-rv">Exemples de primes</span><h2 className="lp-rv" style={d(60)}>Ce que la prime CEE peut <em>financer</em></h2></div></div>
      <div className="lp-primes">
        {PRIMES.map(([t, s, v], i) => (
          <a key={t} href="#etude" className="lp-bezel lp-prime lp-rv" style={d(i * 70)} data-testid={`prime-card-${i + 1}`}>
            <div className="lp-core">
              <small>{t} de {s}</small>
              <strong>≈ {v}</strong>
              <span>de prime CEE estimée</span>
              <span className="lp-more">Étude gratuite<ArrowRight /></span>
            </div>
          </a>
        ))}
      </div>
      <p className="lp-mention lp-rv" data-testid="prime-mention">Estimations indicatives (zone climatique H2), Coup de pouce Chauffage inclus. Le montant exact et votre reste à charge sont chiffrés dans l'étude gratuite.</p>
    </div>
  </section>
);

const eur = (n) => `${Number(n).toLocaleString("fr-FR")} €`;

export const Realisations = () => {
  const items = REALISATIONS.filter((r) => r.publie);
  if (!items.length) return null;
  return (
    <section className="lp-section" id="realisations" data-testid="realisations">
      <div className="lp-wrap">
        <div className="lp-head"><div><span className="lp-eyebrow lp-rv">Nos réalisations</span><h2 className="lp-rv" style={d(60)}>Des projets <em>financés</em>, des charges en baisse</h2></div></div>
        <div className="lp-reals">
          {items.map((r, i) => (
            <article key={r.titre} className="lp-bezel lp-rv" style={d((i % 3) * 70)} data-testid={`realisation-${i + 1}`}>
              <div className="lp-core">
                <div className="lp-real-ph"><figure><img src={r.photoAvant} alt={`${r.titre} avant travaux`} loading="lazy" /><figcaption>Avant</figcaption></figure><figure><img src={r.photoApres} alt={`${r.titre} après travaux`} loading="lazy" /><figcaption>Après</figcaption></figure></div>
                <span className="lp-fiche">{r.typeBatiment} · {r.ville}</span>
                <h3>{r.titre}</h3>
                <dl>
                  <div><dt>Ancienne énergie</dt><dd>{r.ancienneEnergie}</dd></div>
                  <div><dt>Surface / logements</dt><dd>{r.surface}</dd></div>
                  <div><dt>Puissance PAC</dt><dd>{r.puissancePac}</dd></div>
                  <div><dt>Prime CEE</dt><dd>{eur(r.primeCee)}</dd></div>
                  <div><dt>Reste à charge</dt><dd className="lp-real-zero">{eur(r.resteACharge)}</dd></div>
                  <div><dt>Économie annuelle</dt><dd>{r.economieAnnuelle}</dd></div>
                </dl>
                <p className="lp-real-ok"><Check />Dossier CEE monté par Airthermo</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
